import { Injectable, signal } from "@angular/core";
import { OrganizationMicroService } from "../../services/organization.micro.service";
import type { StudentEngagementOrg } from "./StudentEngagementOrg";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { MicroUtil } from "../../services/MicroServices/micro.util";
import { S25Util } from "../../util/s25-util";
import type { S25WsMicroOrganization } from "../../services/MicroServices/S25WsMicroOrganization";
import type { SeEvent } from "./event/se.event.details.view.component";
import {
    DEFAULT_PAGE_SIZE,
    SeSearchService,
    SEEventDocument,
    SESearchResponse,
    SEOrganizationDocument,
} from "./search/se.search.service";
import { SeOrgStatusService } from "./se.org.status.service";
import { ContactRole, ContactRoleId, OrganizationService } from "../../services/organization.service";
import { Item } from "../../pojo/Item";
import { Category } from "../../pojo/Category";
import type { Organization } from "../../pojo/Organization";
import { getLogoUrlCoordinated, getLogoUrlsCoordinated, type LogoObjectRef } from "./logo-url.coordinator";
import { enrichOrgMemberRoleNames } from "./organization/se.org.member.role.names.util";
import { ObjectDetailsUtil } from "../../util/object.details.util";

type ItemContact = S25WsMicroOrganization.ItemContact;

/** Extra organization tile query fields for {@link StudentEngagementService.getMyOrgs}; page/paginate are separate arguments. */
export type StudentEngagementMyOrgsSearchParams = Omit<
    Partial<Organization.WsQueryParams>,
    "page" | "page_size" | "paginate"
>;

@Injectable()
export class StudentEngagementService {
    public static async getOrganizationDetails(itemId: number): Promise<StudentEngagementOrg.details> {
        const [organization, upcomingEvents, ratings, statusPayload] = await Promise.all([
            OrganizationMicroService.getOrganizationIncludes([itemId], ["contacts", "attributes"], true),
            StudentEngagementService.getUpcomingEventsByOrg(itemId),
            OrganizationService.getOrganizationRatings([itemId]), //todo still load if this doesn't
            SeOrgStatusService.resolveEffects(itemId), //todo still load if this doesn't
        ]);
        const details = organization.data.items[0];
        const expandedInfo = organization.expandedInfo?.[0];
        const mission =
            details.attributes.find(
                (attr) => attr.attributeId === CustomAttributes.system.StudentEngagementOrgMission.id,
            )?.value || "No mission statement provided";

        const membersArr: ContactRole[] = S25Util.array.forceArray(details.contacts).map((cont: ItemContact) => {
            const contactDetails = MicroUtil.getContactDetails(cont.contactId, expandedInfo?.contacts ?? []);
            return {
                itemId: cont.contactId,
                itemName: contactDetails.fullName,
                isPrimary: cont.contactId === details.primeContact,
                itemTitle: "",
                itemEmail: contactDetails.email,
                itemTypeId: Item.Ids.Contact,
                role: {
                    itemId: cont.contactRoleId ?? ContactRoleId.None,
                    itemName: "",
                },
            };
        });
        const filteredMembers = await ObjectDetailsUtil.filterContactRoleAssignments(membersArr);
        await enrichOrgMemberRoleNames(filteredMembers);
        const members = signal<ContactRole[]>(filteredMembers);

        const attributeNames = new Map(
            (expandedInfo?.attributes ?? []).map((attribute) => [attribute.attributeId, attribute.attributeName]),
        );
        const attributes = S25Util.array.forceArray(details.attributes ?? []).map(
            (attribute): StudentEngagementOrg.NamedAttribute => ({
                ...attribute,
                attributeName: attributeNames.get(attribute.attributeId) ?? String(attribute.attributeId),
            }),
        );

        const ratingRow = S25Util.array.forceArray(ratings)[0];
        const orgRating =
            ratingRow && ratingRow.ratingId > 0
                ? { itemId: ratingRow.ratingId, itemName: ratingRow.ratingName }
                : undefined;

        return {
            name: details.organizationName,
            mission: mission,
            primaryContact: filteredMembers.find((member) => member.itemId === details.primeContact),
            members: members,
            imageAttr: details.attributes.find(
                (attr) => attr.attributeId === CustomAttributes.system.StudentEngagementOrgLogo.id,
            ),
            attributes,
            upcomingEvents: <SeEvent[]>upcomingEvents,
            orgRating,
            statusEffects: statusPayload?.statusEffects ?? [],
            statusMessages: statusPayload?.statusMessages ?? [],
        };
    }

    public static async getUpcomingEventsByOrg(orgId: number): Promise<SeEvent[]> {
        const [events, error] = await S25Util.Maybe(
            SeSearchService.getUpcomingEvents(1, DEFAULT_PAGE_SIZE, "T", { organization_id: [orgId] }),
        );

        if (error) {
            console.error(error);
            return [];
        }
        return events.items.map((event: SEEventDocument) => ({
            itemId: event.itemId,
            itemName: event.itemName,
            date: event.startDate,
            description: event.description,
            location: event.spaces?.[0]?.itemName,
            locations: event.spaces?.map((space) => space.itemName),
            hostOrg: {
                itemId: Number(event.hostOrgId),
                itemName: event.hostOrgName,
            },
            reservationId: event.reservationId,
            occurrences: event.occurrences,
        }));
    }

    public static getMyOrgsSearchCriteria() {
        return <Organization.WsQueryParams>{
            my_orgs: true,
            attribute_id: CustomAttributes.system.StudentEngagementOrg.id,
            attribute_value: "true",
        };
    }

    public static async getMyOrgs(
        page: number = 1,
        pageSize: number = DEFAULT_PAGE_SIZE,
        paginateKey: string = "T",
        search?: StudentEngagementMyOrgsSearchParams,
    ): Promise<SESearchResponse<SEOrganizationDocument>> {
        const queryParams: Organization.WsQueryParams = {
            ...StudentEngagementService.getMyOrgsSearchCriteria(),
            ...search,
        };
        return SeSearchService.getOrganizations(queryParams, {
            page: page,
            page_size: pageSize,
            paginate: paginateKey,
        });
    }

    public static async getAllOrgs(
        page: number = 1,
        pageSize: number = DEFAULT_PAGE_SIZE,
        paginateKey: string = "T",
    ): Promise<SESearchResponse<SEOrganizationDocument>> {
        return SeSearchService.getOrganizations(
            { attribute_id: CustomAttributes.system.StudentEngagementOrg.id, attribute_value: "true" },
            { page: page, page_size: pageSize, paginate: paginateKey },
        );
    }

    public static async getPromotedOrgs(
        page: number = 1,
        pageSize: number = DEFAULT_PAGE_SIZE,
        paginateKey: string = "T",
    ): Promise<SESearchResponse<SEOrganizationDocument>> {
        return SeSearchService.getOrganizations(
            { category_id: [Category.SystemDefined.Connect25OrgPromoted] },
            { page: page, page_size: pageSize, paginate: paginateKey },
        );
    }

    public static getLogoUrl(itemTypeId: number, itemId: number): Promise<string | undefined> {
        return getLogoUrlCoordinated(itemTypeId, itemId);
    }

    public static getLogoUrls(refs: LogoObjectRef[]): Promise<Map<string, string | undefined>> {
        return getLogoUrlsCoordinated(refs);
    }
}
